import type { Block, SurfaceSpec } from "./surface-spec";
import { blockTypeOrigin, CLAIM_BEARING_TYPES, depthCap, isRegisteredBlockType, isSurfaceDepth } from "./block-registry";

/**
 * P0 validator — the seam that makes "don't fork the catalog" a hard constraint,
 * not a promise. It enforces the five schema seams:
 *   1. copy ⊥ binding  → claims only enter via an approved proofRef
 *   2. type ∈ registry  → block.type must be a shared atom or gemsales allowlist
 *   3. depth caps       → blocks.length ≤ cap(depth)
 *   4. no raw HTML      → render owns markup (injection + off-brand guard)
 *   5. binding refs     → resolve against the catalog (when one is supplied)
 */

export type IssueSeverity = "error" | "warning";

export interface SurfaceSpecIssue {
  code: string;
  message: string;
  severity: IssueSeverity;
  blockId?: string;
}

export interface ValidationResult {
  /** true when there are no error-severity issues. */
  ok: boolean;
  issues: SurfaceSpecIssue[];
}

/**
 * Catalog seam — the binder verifies refs against the real store catalog.
 * At P0 it is optional; without it, refs are only checked for presence.
 */
export interface Catalog {
  hasProduct(ref: string): boolean;
  hasProof(ref: string): boolean;
}

const HTML_TAG = /<\/?[a-z][\s\S]*?>/i;

export function validateSurfaceSpec(spec: SurfaceSpec, catalog?: Catalog): ValidationResult {
  const issues: SurfaceSpecIssue[] = [];
  const push = (code: string, message: string, severity: IssueSeverity, blockId?: string): void => {
    issues.push({ code, message, severity, ...(blockId ? { blockId } : {}) });
  };

  // ---- top-level shape ----
  if (spec.version !== "1.0") push("bad_version", `unsupported version "${String(spec.version)}"`, "error");
  if (!spec.store) push("missing_store", "spec.store (tenant) is required", "error");
  if (!spec.style || !spec.style.packRef) {
    push("missing_stylepack", "style.packRef is required (style resolves at render, never inlined)", "error");
  }

  const depth = spec.intent ? spec.intent.depth : undefined;
  if (!isSurfaceDepth(depth)) {
    push("bad_depth", "intent.depth must be 1, 2, 3, or 4", "error");
  }

  const blocks: Block[] = Array.isArray(spec.blocks) ? spec.blocks : [];
  if (blocks.length === 0) push("empty_surface", "a surface needs at least one block", "error");

  // ---- seam 3: depth caps ----
  if (isSurfaceDepth(depth)) {
    const cap = depthCap(depth);
    if (blocks.length > cap) {
      push("depth_overflow", `depth ${depth} allows ≤ ${cap} blocks, got ${blocks.length}`, "error");
    }
  }

  let hasUnprovenClaim = false;
  const seenIds = new Set<string>();

  for (const block of blocks) {
    if (validateBlock(block, catalog, push, seenIds)) hasUnprovenClaim = true;
  }

  // ---- seam 1: a claim with no proofRef may not be declared "clean" ----
  if (hasUnprovenClaim && spec.guardrail && spec.guardrail.status === "clean") {
    push(
      "claim_without_proof",
      'a claim-bearing block has no binding.proofRef but guardrail.status is "clean"; set it to "needs_review" or attach an approved proofRef',
      "error",
    );
  }

  return { ok: issues.every((issue) => issue.severity !== "error"), issues };
}

/** Returns true when the block asserts a claim without a proofRef. */
function validateBlock(
  block: Block,
  catalog: Catalog | undefined,
  push: (code: string, message: string, severity: IssueSeverity, blockId?: string) => void,
  seenIds: Set<string>,
): boolean {
  const blockId = block.id;

  if (!blockId) {
    push("missing_block_id", "block.id is required", "error");
  } else if (seenIds.has(blockId)) {
    push("duplicate_block_id", `duplicate block id "${blockId}"`, "error", blockId);
  } else {
    seenIds.add(blockId);
  }

  // ---- seam 2: type must be registered ----
  if (!isRegisteredBlockType(block.type)) {
    push(
      "unknown_block_type",
      `block.type "${String(block.type)}" is not in the section-runtime registry or the gemsales allowlist`,
      "error",
      blockId,
    );
    return false; // cannot reason further about an unknown block
  }

  const expectedOrigin = blockTypeOrigin(block.type);
  // `gemchat` is the pre-rename spelling of `gemsales`. The type no longer allows it, but surface specs
  // cached in Redis before the rename, and the untyped fallback blocks gemboss builds in its own surface
  // route, still carry it at runtime; reading it as `gemsales` keeps those specs rendering.
  const origin = (block.origin as string) === "gemchat" ? "gemsales" : block.origin;
  if (expectedOrigin && origin !== expectedOrigin) {
    push(
      "origin_mismatch",
      `block.type "${block.type}" is a ${expectedOrigin} atom but origin is "${String(block.origin)}"`,
      "error",
      blockId,
    );
  }

  const slots: BlockSlotsLike = block.slots ?? {};

  // ---- seam 4: no raw HTML anywhere in slots ----
  if (containsHtml(slots)) {
    push(
      "raw_html_in_slots",
      "slots must not contain raw HTML — the renderer owns markup (prevents injection and off-brand output)",
      "error",
      blockId,
    );
  }

  // ---- seam 5: binding refs ----
  const products = block.binding?.products;
  if (products) {
    for (const ref of products) {
      if (!ref) {
        push("empty_product_ref", "binding.products contains an empty ref", "error", blockId);
      } else if (catalog && !catalog.hasProduct(ref)) {
        push("unknown_product_ref", `binding.products ref "${ref}" not found in catalog`, "error", blockId);
      }
    }
  }

  const proofRef = block.binding?.proofRef;
  if (proofRef && catalog && !catalog.hasProof(proofRef)) {
    push("unknown_proof_ref", `binding.proofRef "${proofRef}" is not in the approved proof library`, "error", blockId);
  }

  // ---- seam 1: claim ⇒ proofRef ----
  const isClaim = CLAIM_BEARING_TYPES.has(block.type) || slots.claim === true;
  return isClaim && !proofRef;
}

type BlockSlotsLike = { claim?: boolean; [key: string]: unknown };

function containsHtml(value: unknown): boolean {
  if (typeof value === "string") return HTML_TAG.test(value);
  if (Array.isArray(value)) return value.some(containsHtml);
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some(containsHtml);
  }
  return false;
}
